// ec-results.js — Step 3 rendering.
//
// Three tiers in the order DESIGN.md fixes them:
//   3.1 the bill       — count-up total, slab breakdown table + stacked bar
//   3.2 the mythbuster — per-appliance ranking and the big % reveal
//   3.3 the savings    — Chunk 6 owns this; we only leave the mount point
//
// All figures come from ec-core.js. Nothing here recomputes a taka.

(function (root) {
  'use strict';

  var COUNT_UP_MS = 900; // slower than the Step 2 meter: this is the payoff
  var UNITS_WARN_AT = 2000; // PROJECT.md 7.4

  var els = {};
  var countUpRaf = null;
  var displayedTotal = 0;
  var booted = false;

  /* ------------------------------------------------------------- helpers */

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function W() {
    return root.EC_WIZARD;
  }

  function i18n() {
    return root.EC_I18N;
  }

  function label(obj) {
    return i18n().pick(obj);
  }

  /** International digits with thousands separators (DESIGN.md 1.5). */
  function fmtTaka(n) {
    return Math.round(n).toLocaleString('en-US');
  }

  function fmtUnits(n) {
    return (Math.round(n * 10) / 10).toLocaleString('en-US');
  }

  function fmtRate(n) {
    return n.toFixed(2);
  }

  function pct(fraction) {
    return Math.round(fraction * 100);
  }

  function reducedMotion() {
    return root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Never show Infinity to a user (DESIGN.md hard-don't #3). */
  function slabLabel(row) {
    if (row.isLifeline) return i18n().t('breakdown.lifeline', { max: row.max });
    var lo = row.min === 0 ? 1 : row.min;
    if (row.max === Infinity || !isFinite(row.max)) return lo + '+';
    return lo + '–' + row.max;
  }

  function slabVar(row) {
    if (row.isLifeline) return 'var(--ec-slab-1)';
    return 'var(--ec-slab-' + Math.min(6, row.slabIndex + 1) + ')';
  }

  /* --------------------------------------------------------------- 3.1 */

  function countUp(to) {
    if (countUpRaf) cancelAnimationFrame(countUpRaf);
    var from = displayedTotal;
    var hidden = typeof document !== 'undefined' && document.hidden;

    if (reducedMotion() || hidden || Math.abs(to - from) < 1) {
      displayedTotal = to;
      if (els.total) els.total.textContent = fmtTaka(to);
      return;
    }

    var start = performance.now();
    function frame(now) {
      var p = Math.min(1, (now - start) / COUNT_UP_MS);
      var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic — settles, not bounces
      if (els.total) els.total.textContent = fmtTaka(from + (to - from) * eased);
      if (p < 1) {
        countUpRaf = requestAnimationFrame(frame);
      } else {
        displayedTotal = to;
        countUpRaf = null;
      }
    }
    countUpRaf = requestAnimationFrame(frame);
  }

  function renderBar(breakdown) {
    if (!els.bar) return;
    els.bar.innerHTML = '';
    var total = breakdown.reduce(function (a, r) { return a + r.units; }, 0);
    if (total <= 0) return;

    breakdown.forEach(function (row) {
      var seg = document.createElement('span');
      seg.className = 'ec-break__seg';
      seg.style.width = (row.units / total) * 100 + '%';
      seg.style.background = slabVar(row);
      els.bar.appendChild(seg);
    });
  }

  function renderSlabRows(breakdown) {
    if (!els.rows) return;
    els.rows.innerHTML = '';

    breakdown.forEach(function (row) {
      var tr = document.createElement('tr');

      var th = document.createElement('th');
      th.setAttribute('scope', 'row');
      th.className = 'ec-table__slab';
      // The swatch repeats the bar's colour, but the row text carries the
      // meaning — colour is never the only channel (DESIGN.md 1.7).
      var dot = document.createElement('span');
      dot.className = 'ec-table__dot';
      dot.style.background = slabVar(row);
      dot.setAttribute('aria-hidden', 'true');
      th.appendChild(dot);
      th.appendChild(document.createTextNode(slabLabel(row)));

      tr.appendChild(th);
      tr.appendChild(cell(fmtUnits(row.units)));
      tr.appendChild(cell(fmtRate(row.rate)));
      tr.appendChild(cell(fmtTaka(row.subtotal)));
      els.rows.appendChild(tr);
    });
  }

  function cell(text) {
    var td = document.createElement('td');
    td.className = 'ec-table__num ec-num';
    td.textContent = text;
    return td;
  }

  function chargeRow(label, value, opts) {
    opts = opts || {};
    var dt = document.createElement('dt');
    dt.textContent = label;
    var dd = document.createElement('dd');
    dd.className = 'ec-num';
    dd.textContent = (opts.negative ? '−' : '') + '৳' + fmtTaka(Math.abs(value));
    if (opts.tone) {
      dt.setAttribute('data-tone', opts.tone);
      dd.setAttribute('data-tone', opts.tone);
    }
    if (opts.strong) {
      dt.setAttribute('data-strong', 'true');
      dd.setAttribute('data-strong', 'true');
    }
    els.charges.appendChild(dt);
    els.charges.appendChild(dd);
  }

  function renderCharges(bill) {
    if (!els.charges) return;
    els.charges.innerHTML = '';

    var t = i18n().t;
    chargeRow(t('charges.energy'), bill.energy.total);
    chargeRow(t('charges.demand'), bill.demand);
    // A fixed monthly charge, so it is shown even at ৳0 — a customer comparing
    // this against a real bill needs to see the line exists either way. When
    // the phase's rent is unknown the row says so instead of showing a figure.
    if (bill.meterRentKnown) {
      chargeRow(t('charges.meterRent'), bill.meterRent);
    } else {
      chargeRow(t('charges.meterRentUnknown'), 0, { tone: 'muted' });
    }
    chargeRow(t('charges.vat'), bill.vat);
    if (bill.prepaid > 0) {
      chargeRow(t('charges.prepaidRebate'), bill.prepaid, { negative: true, tone: 'save' });
    }
    // A surcharge, not a forfeited rebate: it only appears when the user has
    // said they pay late, and it reads as an added cost, in the cost tone.
    if (bill.lateSurcharge > 0) {
      chargeRow(t('charges.lateSurcharge'), bill.lateSurcharge, { tone: 'cost' });
    }
    chargeRow(t('charges.total'), bill.grandTotal, { strong: true });
  }

  /* --------------------------------------------------------------- 3.2 */

  // Devices people underestimate: huge draw, short runtime. These are what the
  // myth-break line is for — nobody is surprised that an AC uses power.
  var SNEAKY = {
    geyser: true, instant_geyser: true, kettle: true, induction: true,
    microwave: true, oven: true, iron: true, hair_dryer: true, pump_1hp: true
  };

  function isAC(key) {
    return String(key).indexOf('ac_') === 0;
  }

  function renderMyth(rows, marginalRate) {
    if (!els.mythRows) return;
    els.mythRows.innerHTML = '';

    if (!rows.length) {
      els.reveal.textContent = '';
      els.bust.hidden = true;
      return;
    }

    var top = rows[0];
    var maxUnits = top.units;

    rows.forEach(function (row, i) {
      var li = document.createElement('li');
      li.className = 'ec-myth__row';
      if (i === 0) li.setAttribute('data-top', 'true');

      var head = document.createElement('div');
      head.className = 'ec-myth__head';

      var name = document.createElement('span');
      name.className = 'ec-myth__name';
      name.textContent = label(row);
      if (row.qty > 1) {
        var q = document.createElement('span');
        q.className = 'ec-myth__qty ec-num';
        q.textContent = '×' + row.qty;
        name.appendChild(document.createTextNode(' '));
        name.appendChild(q);
      }

      var cost = document.createElement('span');
      cost.className = 'ec-myth__cost ec-num';
      cost.textContent = '৳' + fmtTaka(row.cost);

      head.appendChild(name);
      head.appendChild(cost);

      var track = document.createElement('div');
      track.className = 'ec-myth__track';
      var fill = document.createElement('span');
      fill.className = 'ec-myth__fill';
      fill.style.width = (maxUnits > 0 ? (row.units / maxUnits) * 100 : 0) + '%';
      track.appendChild(fill);

      var meta = document.createElement('p');
      meta.className = 'ec-myth__meta';
      // Restate the bar as text: the chart is never the only channel.
      var duty = row.dutyCycle < 1
        ? ' (' + i18n().t('tile.dutyCycle', { percent: Math.round(row.dutyCycle * 100) }) + ')'
        : '';
      meta.textContent = i18n().t('myth.metaLine', {
        units: fmtUnits(row.units),
        percent: pct(row.share),
        watts: row.watts,
        duty: duty
      });

      li.appendChild(head);
      li.appendChild(track);
      li.appendChild(meta);
      els.mythRows.appendChild(li);
    });

    // ---- the big reveal. Three fragments (pre/mid/post) rather than one
    // interpolated sentence, so the appliance name and the percentage keep
    // their <strong> emphasis in both languages — see myth.reveal.* in the
    // locale files, which mirror this exact segmentation.
    els.reveal.innerHTML = '';
    els.reveal.appendChild(document.createTextNode(i18n().t('myth.reveal.pre') + ' '));
    var strongName = document.createElement('strong');
    strongName.textContent = label(top);
    els.reveal.appendChild(strongName);
    els.reveal.appendChild(document.createTextNode(' ' + i18n().t('myth.reveal.mid') + ' '));
    var strongPct = document.createElement('strong');
    strongPct.className = 'ec-myth__big ec-num';
    strongPct.textContent = pct(top.share) + '%';
    els.reveal.appendChild(strongPct);
    els.reveal.appendChild(
      document.createTextNode(' ' + i18n().t('myth.reveal.post', { cost: fmtTaka(top.cost) }))
    );

    // ---- the myth-break line
    var bust = mythBust(rows);
    if (bust) {
      els.bust.textContent = bust;
      els.bust.hidden = false;
    } else {
      els.bust.hidden = true;
      els.bust.textContent = '';
    }
  }

  /**
   * The myth-break, per PROJECT.md 3.2. Only ever states something true about
   * THIS user's list — if there is no surprise to report, it stays silent
   * rather than inventing one.
   */
  function mythBust(rows) {
    var t = i18n().t;
    var top = rows[0];
    var acRow = null;
    var i;
    for (i = 0; i < rows.length; i++) {
      if (isAC(rows[i].key)) { acRow = rows[i]; break; }
    }

    // Case 1: the user has an AC and something else still beats it.
    if (acRow && !isAC(top.key) && acRow.units > 0) {
      var ratio = top.units / acRow.units;
      // "3.3x more" reads better than "227% more" once the gap is large.
      var gap = ratio >= 2
        ? t('myth.bustGapMultiplier', { n: Math.round(ratio * 10) / 10 })
        : t('myth.bustGapPercent', { n: Math.round((ratio - 1) * 100) });
      return t('myth.bustAcBeaten', { watts: top.watts, name: label(top), gap: gap });
    }

    // Case 2: AC leads, but a short-runtime device is quietly close behind.
    if (acRow && isAC(top.key)) {
      for (i = 1; i < rows.length; i++) {
        var r = rows[i];
        if (SNEAKY[r.key] && r.hours <= 2 && r.units / top.units >= 0.25) {
          return t('myth.bustSneaky', {
            name: label(r),
            hours: fmtUnits(r.hours),
            percent: Math.round((r.units / top.units) * 100),
            watts: r.watts
          });
        }
      }
      return null;
    }

    // Case 3: no AC at all, but a high-wattage short-runtime device leads.
    if (!acRow && SNEAKY[top.key] && top.hours <= 2) {
      return t('myth.bustNoAc', { hours: fmtUnits(top.hours), name: label(top), watts: top.watts });
    }

    return null;
  }

  /* ------------------------------------------------------------- render */

  function render() {
    if (!root.EC || !W()) return;
    var state = W().state;
    var selections = state.appliances || [];

    // Run 4 Phase 4: only LT-A computes — EC.calcBill() throws for anything
    // else, so this is checked first and takes priority over the "nothing
    // selected" empty state below (there's no bill either way, but the
    // reason differs and the copy should say so).
    var functional = !W().categoryFunctional || W().categoryFunctional();
    if (els.unsupported) els.unsupported.hidden = functional;
    if (!functional) {
      if (els.empty) els.empty.hidden = true;
      if (els.body) els.body.hidden = true;
      if (countUpRaf) cancelAnimationFrame(countUpRaf);
      countUpRaf = null;
      displayedTotal = 0;
      return;
    }

    var hasAny = root.EC.totalUnits(selections) > 0;
    if (els.empty) els.empty.hidden = hasAny;
    if (els.body) els.body.hidden = !hasAny;

    if (!hasAny) {
      // Clear rather than just hide. A stale figure left in the DOM would make
      // the next count-up animate DOWN from the old number, and would leave the
      // over-usage warning armed for a list that no longer exists.
      if (countUpRaf) cancelAnimationFrame(countUpRaf);
      countUpRaf = null;
      displayedTotal = 0;
      if (els.total) els.total.textContent = '0';
      if (els.units) els.units.textContent = '0';
      if (els.warning) els.warning.hidden = true;
      if (els.announce) els.announce.textContent = '';
      return;
    }

    var units = root.EC.totalUnits(selections);
    var bill = root.EC.calcBill(W().getBillInputs(units));

    // 3.1
    countUp(bill.grandTotal);
    if (els.units) els.units.textContent = fmtUnits(units);
    if (els.marginal) els.marginal.textContent = fmtRate(bill.marginalRate);
    if (els.marginalEcho) els.marginalEcho.textContent = fmtRate(bill.marginalRate);
    if (els.warning) els.warning.hidden = units <= UNITS_WARN_AT;

    // The device's severity state — identical rule to the Step 2 meter, so the
    // two readouts can never disagree about what the meter is showing.
    if (els.bill) {
      var cat = root.EC.getCategory(state.company, state.category);
      var sev = root.EC.severityFor(bill.marginalIndex, cat);
      els.bill.setAttribute('data-ec-severity', sev);
      if (els.flagText) {
        var rate = fmtRate(bill.marginalRate);
        els.flagText.textContent = sev === 'danger'
          ? i18n().t('step3.bill.flag.danger', { rate: rate })
          : i18n().t('step3.bill.flag.warn', { rate: rate });
      }
    }

    renderBar(bill.energy.breakdown);
    renderSlabRows(bill.energy.breakdown);
    if (els.energyTotal) els.energyTotal.textContent = '৳' + fmtTaka(bill.energy.total);
    renderCharges(bill);

    // 3.2 — duty-cycle-corrected units come straight from the math layer.
    var myth = root.EC.applianceBreakdown(selections, bill.marginalRate);
    renderMyth(myth.rows, bill.marginalRate);

    if (els.announce) {
      els.announce.textContent = i18n().t('meter.announce', {
        total: fmtTaka(bill.grandTotal),
        units: fmtUnits(units)
      });
    }

    // 3.3 — Chunk 6 subscribes to this.
    if (root.EC_RECOMMEND && root.EC_RECOMMEND.render) {
      root.EC_RECOMMEND.render({ bill: bill, units: units, myth: myth });
    }

    return { bill: bill, units: units, myth: myth };
  }

  /* --------------------------------------------------------------- boot */

  function init() {
    if (booted) return;
    els.body = $('[data-ec-results]');
    if (!els.body) return;

    els.empty = $('[data-ec-results-empty]');
    els.unsupported = $('[data-ec-results-unsupported]');
    els.bill = $('[data-ec-bill]');
    els.flagText = els.bill ? els.bill.querySelector('[data-ec-flag-text]') : null;
    els.total = $('[data-ec-bill-total]');
    els.units = $('[data-ec-bill-units]');
    els.marginal = $('[data-ec-bill-marginal]');
    els.marginalEcho = $('[data-ec-bill-marginal-echo]');
    els.announce = $('[data-ec-bill-announce]');
    els.warning = $('[data-ec-units-warning]');
    els.bar = $('[data-ec-break-bar]');
    els.rows = $('[data-ec-break-rows]');
    els.charges = $('[data-ec-charges]');
    els.energyTotal = $('[data-ec-break-energy]');
    els.mythRows = $('[data-ec-myth-rows]');
    els.reveal = $('[data-ec-myth-reveal]');
    els.bust = $('[data-ec-myth-bust]');

    booted = true;

    // NARRATIVE MODE (run 3, Phase 3): the results are always in the document,
    // so they have to stay true while the user edits appliances further up the
    // page — arriving at a stale bill after scrolling would be worse than the
    // old hidden panel.
    //
    // Renders are coalesced to one animation frame: a stepper held down fires
    // `change` continuously, and re-rendering the slab table on every keypress
    // is wasted work on the low-end phones this is built for.
    var pending = null;
    function scheduleRender() {
      if (pending) return;
      pending = (root.requestAnimationFrame || setTimeout)(function () {
        pending = null;
        render();
      }, 16);
    }

    // `change` covers Step-1 edits (company, load, meter type).
    // `recalc` covers appliance edits, which never emit `change` — see the
    // comment on the listeners map in ec-wizard.js. Missing this one left the
    // bill stale while the pinned meter moved.
    W().on('change', scheduleRender);
    W().on('recalc', scheduleRender);

    // Every label on this step is JS-authored (chargeRow, mythbuster, the
    // flag text), so a language toggle needs a full re-render, not just the
    // static [data-i18n] sweep ec-i18n.js already did.
    if (root.EC_I18N && root.EC_I18N.onChange) {
      root.EC_I18N.onChange(scheduleRender);
    }

    W().on('step', function (e) {
      if (!e || e.to !== 3) return;
      // Re-run the count-up when the user deliberately jumps here — this is the
      // payoff moment, and arriving at a static number reads as a page that
      // failed. A scroll-spy update passes fromScroll and never reaches here
      // with a changed step, so scrolling does not restart the animation.
      displayedTotal = 0;
      render();
    });

    // First paint: the bill has to be right before the user ever scrolls to it.
    render();

    var restart = $('[data-ec-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        if (W().reset) W().reset();
        else W().goTo(1);
      });
    }
  }

  root.EC_RESULTS = { init: init, render: render };

  function boot() {
    if (root.EC_I18N && root.EC_I18N.whenReady) root.EC_I18N.whenReady(init);
    else init();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
